import $ from "jquery";
import {gsap} from "gsap/all";
import {Action} from "./Action";
import {cloneSvg} from "../SvgBank";
import {CircleTimer} from "../CircleTimer";

// Keep the rotating ship level with the joystick. Same momentum model as the
// align action: the joystick does not tilt the ship directly, it builds
// angular velocity, and the ship keeps spinning after release. The run starts
// with a small spin toward a random side so the player has to counter it right
// away. A dots meter in the action container mirrors the tilt with a moving
// line, centered when the ship is level. After the run duration the ship has
// to sit within the threshold angle to earn the point.
export class StabilizeShipAction extends Action {

  constructor(game, config) {
    super(game, config);

    // All angles are in degrees, measured from level.

    // How quickly holding the joystick builds angular velocity, in degrees per
    // second squared. A longer hold means a faster spin after release.
    this.accel = 16;
    // Speed cap so long holds never make the ship uncontrollable.
    this.maxSpeed = 15;
    // Starting spin speed toward a random side, in degrees per second. High
    // enough that the ship visibly tilts off before the player reacts.
    this.initialSpeed = 6;
    // Power curve softening the tilt limits: outward motion runs at full speed
    // near level and eases to a stop approaching the max tilt, instead of
    // hitting it like a wall. Higher keeps full speed longer before braking.
    this.edgeEasePower = 2;

    // Config from the JSON, with defaults: how long the run lasts, how close to
    // level counts as stable, and how far the ship is allowed to tilt.
    this.durationSec = config.duration !== undefined ? config.duration : 6;
    this.threshold = config.threshold !== undefined ? config.threshold : 5;
    this.maxRotation = config.max_rotation !== undefined ? config.max_rotation : 25;

    // The box that carries the turn of the ship, the same one the entry and the
    // landing swing turn. Everything else about the ship is left alone.
    this.$ship = this.app.getShipAssembly();

    this.$shipContainer = $("#ship-container");
    this.$meter = null;
    this.$indicator = null;

    // Level for this ship, and the tilt it comes in at. Every rotation below is
    // measured from level and drawn on top of it, and the run starts wherever
    // the entry left the ship flying, which is what the player has to correct.
    this.baseRotation = this.app.getShipLevelRotation();
    this.startRotation = config.start_rotation !== undefined ? config.start_rotation : this.app.getShipEntryTilt();

    // The two outer engines idle low for the whole run and the one fighting the
    // tilt goes to a full burn, so the ship is visibly pushed back toward level.
    // Steering left fires the right engine and the other way around. The middle
    // engine is left alone. They are already lit at the idle by the descent, so
    // nothing has to come down when the run starts.
    this.idleThrusterLevel = this.app.getSideThrusterIdleLevel();
    this.steerThrusterLevel = 1;
    // The axis the engines are currently set for, so they are only retuned when
    // the joystick actually changes.
    this.thrusterAxis = null;

    this.rotation = 0;
    this.vel = 0;
    this.elapsed = 0;
    this.running = false;
    this.holdCall = null;

    this.tick = this.tick.bind(this);
  }

  start() {
    // Only the turn. Where the ship sits and how big it is are the placement
    // box's business, and this one is nested inside it.
    gsap.set(this.$ship, {rotation: this.baseRotation + this.rotation});

    // The descent does not pause while the player steers, so the gauge travels
    // from the start state to the end state over the run instead of jumping
    // once the action is done.
    this.applyGaugeEnd(this.durationSec);

    this.timer.start(this.durationSec);

    // Start already spinning toward a random side so the player has to react.
    this.vel = (Math.random() < 0.5 ? -1 : 1) * this.initialSpeed;

    this.running = true;
    this.applyThrusters(this.app.getAxis());
    gsap.ticker.add(this.tick);
  }

  // The side engines at the settings the current joystick direction earns them.
  applyThrusters(axis) {
    if (axis === this.thrusterAxis) return;
    this.thrusterAxis = axis;

    const left = axis > 0 ? this.steerThrusterLevel : this.idleThrusterLevel;
    const right = axis < 0 ? this.steerThrusterLevel : this.idleThrusterLevel;
    this.app.setSideThrusterLevels(left, right);
  }

  // Level enough to earn the point, which is also what the meter and the ship
  // color themselves on while the run is going.
  isStable() {
    return Math.abs(this.rotation) <= this.threshold;
  }

  // The line on the dots track, riding the tilt: middle when the ship is level,
  // outer dots at full tilt either way.
  moveIndicator() {
    gsap.set(this.$indicator, {x: this.indicatorBaseX + this.rotation / this.maxRotation * this.trackHalf});
  }

  build() {
    // The whole meter is one composite design SVG: both joystick icons, the
    // dots track and the green indicator line. Only the indicator moves,
    // tagged here so GSAP and CSS can target it. The dots are the only
    // circles drawn with a fill opacity.
    const $svg = cloneSvg("rotate-joystick-button");
    this.$indicator = $svg.find('rect[fill="#63BD77"]').attr("class", "indicator");
    $svg.find("circle[fill-opacity]").attr("class", "dot");

    this.$meter = $('<div class="stabilize-meter"></div>').append($svg);
    this.$host.append(this.$meter);

    // Track geometry read from the design SVG, in its own units since GSAP
    // moves SVG elements in user units: the indicator rests at the middle of
    // the dots row and reaches the outer dots at full tilt. The tiny base
    // offset corrects the indicator not sitting exactly at that middle in
    // the design file.
    const dotsX = this.$meter.find(".dot").map((i, dot) => parseFloat(dot.getAttribute("cx"))).get();
    const minX = Math.min(...dotsX);
    const maxX = Math.max(...dotsX);
    const indicatorX = parseFloat(this.$indicator.attr("x")) + parseFloat(this.$indicator.attr("width")) / 2;

    this.trackHalf = (maxX - minX) / 2;
    this.indicatorBaseX = (minX + maxX) / 2 - indicatorX;

    // The ship comes in at the angle it was flying at and turning it level is
    // the whole job of the action, so the meter is placed on that tilt here,
    // while the content is still fading in. Waiting for the run to start would
    // show the line centered for a beat and then snap.
    this.rotation = constrain(this.startRotation, -this.maxRotation, this.maxRotation);
    this.moveIndicator();

    // And in the color that tilt earns, so the meter does not come up green on
    // a ship that is anything but level.
    this.$meter.toggleClass("off-track", !this.isStable());

    // Same corner countdown the button actions use, so a run on the joystick
    // shows how long is left just as clearly. Created here so its face fades in
    // with the rest of the content.
    this.timer = new CircleTimer(this.game.$actionContainer.find(".corner-timer"));
  }

  tick(time, deltaMS) {
    if (!this.running) return;

    // Frozen while the "still there?" modal is up, so no time goes by and the
    // ship stays where the player left it.
    if (this.app.isGameplayPaused()) return;

    const dt = deltaMS / 1000;
    this.elapsed += dt;

    // The joystick accelerates the spin instead of tilting the ship directly,
    // so the ship keeps its momentum once the stick is released.
    const axis = this.app.getAxis();
    this.applyThrusters(axis);
    this.vel = constrain(this.vel + axis * this.accel * dt, -this.maxSpeed, this.maxSpeed);

    // Outward motion eases off as the ship nears the tilt limit, so it slows
    // into the edge instead of stopping dead. Motion back toward level is
    // never slowed, recovering always works at full speed.
    let moveVel = this.vel;
    if (this.rotation * this.vel > 0) {
      const edge = Math.min(Math.abs(this.rotation) / this.maxRotation, 1);
      moveVel *= 1 - Math.pow(edge, this.edgeEasePower);
    }
    this.rotation = constrain(this.rotation + moveVel * dt, -this.maxRotation, this.maxRotation);

    gsap.set(this.$ship, {rotation: this.baseRotation + this.rotation});
    this.moveIndicator();

    // Live feedback while the run is going.
    const stable = this.isStable();
    this.$shipContainer.toggleClass("aligned", stable).toggleClass("off-track", !stable);
    this.$meter.toggleClass("off-track", !stable);

    if (this.elapsed >= this.durationSec) this.finish();
  }

  finish() {
    this.running = false;
    gsap.ticker.remove(this.tick);

    const success = this.isStable();
    console.log(`stabilize-ship final rotation ${this.rotation.toFixed(1)} / threshold ±${this.threshold} -> ${success ? "success" : "fail"}`);
    if (success) this.points++;

    this.$shipContainer
      .removeClass("aligned off-track")
      .addClass(success ? "success" : "fail");

    // The meter has no verdict state of its own: the line simply keeps the
    // color it had when the run ended, red exactly when the point is missed.

    this.showVerdict(success);

    // Let the state read for a beat before moving on.
    this.holdCall = gsap.delayedCall(this.game.stateHoldSec, () => this.complete());
  }

  destroy() {
    this.running = false;
    gsap.ticker.remove(this.tick);
    if (this.holdCall) this.holdCall.kill();
    if (this.timer) {
      this.timer.destroy();
      this.timer = null;
    }

    this.$shipContainer.removeClass("aligned off-track success fail");

    // The engines are left as they are: the landing that comes next has
    // already started winding them down, see EDL.startLandingThrottle.

    // Ease the ship back to level for whatever comes next.
    gsap.to(this.$ship, {
      rotation: this.baseRotation,
      duration: 0.5,
      ease: "power1.inOut"
    });
  }
}
